import { forwardRef, type ReactNode } from 'react';
import * as RS from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from './cn.js';
import { controlBase, controlSize, useField } from './Field.js';

/**
 * Select, on Radix rather than a styled `<select>`.
 *
 * A native select cannot be styled inside the popup — on Windows it renders an
 * OS listbox that ignores the theme entirely, which is exactly the thing that
 * makes an otherwise-modern app look dated. Radix gives a real listbox with
 * typeahead, arrow keys, Home/End and correct aria, so the visual upgrade does
 * not cost accessibility.
 */

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  size?: keyof typeof controlSize;
  /** Rendered before the value in the trigger. */
  leading?: ReactNode;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  'aria-label'?: string;
  id?: string;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    value,
    onValueChange,
    options,
    placeholder = 'Select…',
    size = 'md',
    leading,
    className,
    contentClassName,
    disabled,
    id,
    ...rest
  },
  ref,
) {
  const f = useField();

  return (
    <RS.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <RS.Trigger
        ref={ref}
        id={id ?? f?.id}
        aria-describedby={f?.describedBy}
        aria-invalid={f?.invalid || undefined}
        className={cn(
          controlBase,
          controlSize[size],
          'inline-flex items-center justify-between gap-2 whitespace-nowrap text-left',
          'data-[placeholder]:text-ink-tertiary',
          className,
        )}
        // The whole name on hover: in a narrow column a long one ("Security
        // report / suspicious message") is cut to one line, never wrapped --
        // wrapped, it spilled out of a fixed-height control.
        title={options.find((o) => o.value === value)?.label}
        {...rest}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {leading && (
            <span aria-hidden className="shrink-0 text-ink-tertiary [&>svg]:h-3.5 [&>svg]:w-3.5">
              {leading}
            </span>
          )}
          <span className="block min-w-0 truncate">
            <RS.Value placeholder={placeholder} />
          </span>
        </span>
        <RS.Icon asChild>
          <ChevronDown aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
        </RS.Icon>
      </RS.Trigger>

      <RS.Portal>
        <RS.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'anim-content z-50 max-h-[22rem] min-w-[--radix-select-trigger-width] overflow-hidden',
            'rounded-lg border border-line bg-surface-raised p-1 shadow-lg',
            contentClassName,
          )}
        >
          <RS.ScrollUpButton className="flex h-5 items-center justify-center text-ink-tertiary">
            <ChevronUp className="h-3 w-3" />
          </RS.ScrollUpButton>

          <RS.Viewport className="max-h-[20rem]">
            {options.map((o) => (
              <RS.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className={cn(
                  'relative flex cursor-default select-none items-start gap-2 rounded-sm py-1.5 pl-7 pr-2.5',
                  'text-sm outline-none',
                  'data-[highlighted]:bg-brand-tint data-[highlighted]:text-brand',
                  'data-[state=checked]:font-medium',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
                )}
              >
                <RS.ItemIndicator className="absolute left-2 top-2">
                  <Check aria-hidden className="h-3.5 w-3.5" />
                </RS.ItemIndicator>
                <span className="min-w-0">
                  <RS.ItemText>{o.label}</RS.ItemText>
                  {o.hint && (
                    <span className="mt-0.5 block text-2xs text-ink-tertiary">{o.hint}</span>
                  )}
                </span>
              </RS.Item>
            ))}
          </RS.Viewport>

          <RS.ScrollDownButton className="flex h-5 items-center justify-center text-ink-tertiary">
            <ChevronDown className="h-3 w-3" />
          </RS.ScrollDownButton>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  );
});
